import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSettings } from './SettingsContext';
import { useAuth } from './AuthContext';
import { tokens } from '../design-system/tokens';

const ThemeContext = createContext();

// Cottagecore theme — warm paper palette
const lightTheme = {
  bg: '#F7EFD8',
  surface: '#EEE2BF',
  hover: '#FDF7E5',
  bgSecondary: '#EEE2BF',
  border: '#D7C9A1',
  borderStrong: '#BFAB79',
  text: '#2E2109',
  muted: '#6E5A38',
  faint: '#A39068',
  body: '#2E2109',
  card: '#FFFBEC',
};

// Keep dark as cottagecore too — no dark mode for now
const darkTheme = lightTheme;

export const ThemeProvider = ({ children }) => {
  const { settings, settingsLoaded } = useSettings();
  const { isAuthenticated, loading } = useAuth();

  // Track system dark mode preference
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPrefersDark(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Resolve theme: user preference > system preference
  const themeMode = settings?.themeMode || 'system';
  const shouldUseDarkMode =
    themeMode === 'dark' ? true :
    themeMode === 'light' ? false :
    systemPrefersDark;

  useEffect(() => {
    // Apply theme to CSS custom properties
    const theme = shouldUseDarkMode ? darkTheme : lightTheme;
    const root = document.documentElement;

    // Update CSS custom properties
    root.style.setProperty('--color-bg', theme.bg);
    root.style.setProperty('--color-surface', theme.surface);
    root.style.setProperty('--color-hover', theme.hover);
    root.style.setProperty('--color-bg-secondary', theme.bgSecondary);
    root.style.setProperty('--color-border', theme.border);
    root.style.setProperty('--color-border-strong', theme.borderStrong || theme.border);
    root.style.setProperty('--color-text', theme.text);
    root.style.setProperty('--color-text-muted', theme.muted);
    root.style.setProperty('--color-text-faint', theme.faint || theme.muted);
    root.style.setProperty('--color-text-body', theme.body);
    root.style.setProperty('--color-card', theme.card || theme.surface);
    root.style.setProperty('--color-paper-soft', theme.hover);

    // Update body background
    document.body.style.backgroundColor = theme.bg;
    document.body.style.color = theme.text;

    // Add/remove dark theme class
    if (shouldUseDarkMode) {
      document.documentElement.classList.add('dark-theme');
      document.documentElement.classList.remove('light-theme');
    } else {
      document.documentElement.classList.add('light-theme');
      document.documentElement.classList.remove('dark-theme');
    }
  }, [shouldUseDarkMode]);

  const contextValue = {
    isDark: shouldUseDarkMode,
    theme: shouldUseDarkMode ? darkTheme : lightTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;